import React, {createContext, useContext, useState} from 'react';
import {Form, Input, message, Modal, Select} from 'antd';
import {contactMessageService} from '../../services/contactMessageService';
import {useI18n} from '../../i18n/useI18n';

interface ContactContextValue {
    openContact: () => void;
}

const ContactContext = createContext<ContactContextValue>({
    openContact: () => {
    }
});

export const useContact = () => useContext(ContactContext);

export const ContactProvider: React.FC<{ children: React.ReactNode }> = ({children}) => {
    const {t} = useI18n();
    const [visible, setVisible] = useState(false);
    const [form] = Form.useForm();
    const [submitting, setSubmitting] = useState(false);

    const openContact = () => setVisible(true);

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            setSubmitting(true);
            await contactMessageService.submit({
                subject: values.subject,
                content: values.content,
                salutation: values.name,
                contactEmail: values.contactEmail,
                phone: values.phone || undefined,
            });
            message.success(t('site.contactModal.success'));
            form.resetFields();
            setVisible(false);
        } catch (error: any) {
            if (error.errorFields) return;
            message.error(error?.response?.data?.message || t('site.contactModal.fail'));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <ContactContext.Provider value={{openContact}}>
            {children}
            <Modal
                title={t('site.contactModal.title')}
                open={visible}
                onOk={handleSubmit}
                onCancel={() => setVisible(false)}
                confirmLoading={submitting}
                okText={t('site.contactModal.okText')}
                cancelText={t('site.contactModal.cancelText')}
                destroyOnHidden
            >
                <Form form={form} layout="vertical" requiredMark={false} style={{marginTop: 16}}>
                    <Form.Item
                        name="name"
                        label={t('site.contactModal.label.name')}
                        rules={[{required: true, message: t('site.contactModal.rule.name')}]}
                    >
                        <Input placeholder={t('site.contactModal.placeholder.name')} size="large"/>
                    </Form.Item>
                    <Form.Item
                        name="contactEmail"
                        label={t('site.contactModal.label.email')}
                        rules={[
                            {required: true, message: t('site.contactModal.rule.email')},
                            {type: 'email', message: t('site.contactModal.rule.emailType')},
                        ]}
                    >
                        <Input placeholder={t('site.contactModal.placeholder.email')} size="large"/>
                    </Form.Item>
                    <Form.Item
                        name="phone"
                        label={t('site.contactModal.label.phone')}
                    >
                        <Input placeholder={t('site.contactModal.placeholder.phone')} size="large"/>
                    </Form.Item>
                    <Form.Item
                        name="subject"
                        label={t('site.contactModal.label.subject')}
                        rules={[{required: true, message: t('site.contactModal.rule.subject')}]}
                    >
                        <Select
                            placeholder={t('site.contactModal.placeholder.subject')}
                            size="large"
                            options={[
                                {label: t('site.contactModal.subject.product'), value: '产品咨询'},
                                {label: t('site.contactModal.subject.cooperation'), value: '合作意向'},
                                {label: t('site.contactModal.subject.tech'), value: '技术问题'},
                                {label: t('site.contactModal.subject.feedback'), value: '意见反馈'},
                                {label: t('site.contactModal.subject.other'), value: '其他'},
                            ]}
                        />
                    </Form.Item>
                    <Form.Item
                        name="content"
                        label={t('site.contactModal.label.content')}
                        rules={[{required: true, message: t('site.contactModal.rule.content')}]}
                    >
                        <Input.TextArea placeholder={t('site.contactModal.placeholder.content')} rows={4} size="large"/>
                    </Form.Item>
                </Form>
            </Modal>
        </ContactContext.Provider>
    );
};
